/**
 * DrillDownContext - Navigation state management for chart drill-down capabilities
 *
 * Provides:
 * - Breadcrumb navigation tracking
 * - URL state synchronization for deep linking
 * - Navigation history with back/forward/reset
 * - Loading states for drill-down data fetching
 * - Keyboard navigation support (Escape/Backspace)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DrillDownData } from '../types/charts';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface Breadcrumb {
  id: string;
  label: string;
  level: string;
  data: DrillDownData['data'];
  timestamp: number;
}

export interface DrillDownState {
  breadcrumbs: Breadcrumb[];
  currentView: string;
  currentData: DrillDownData['data'] | null;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface DrillDownContextType extends DrillDownState {
  // Navigation actions
  drillDown: (level: string, label: string, data: DrillDownData['data']) => void;
  goBack: () => void;
  goToLevel: (levelIndex: number) => void;
  reset: () => void;

  // Modal control
  openDrillDown: (level: string, label: string, data: DrillDownData['data']) => void;
  closeDrillDown: () => void;

  // Loading states
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // URL state
  getShareableUrl: () => string;
  loadFromUrl: (params: URLSearchParams) => void;
}

// Default state
const defaultState: DrillDownState = {
  breadcrumbs: [],
  currentView: 'overview',
  currentData: null,
  isOpen: false,
  isLoading: false,
  error: null,
};

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const DrillDownContext = createContext<DrillDownContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface DrillDownProviderProps {
  children: ReactNode;
  syncWithUrl?: boolean;
}

export const DrillDownProvider: React.FC<DrillDownProviderProps> = ({
  children,
  syncWithUrl = true
}) => {
  const [state, setState] = useState<DrillDownState>(defaultState);
  const [searchParams, setSearchParams] = useSearchParams();

  // Generate unique ID for breadcrumb
  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Drill down to a new level
  const drillDown = useCallback((level: string, label: string, data: DrillDownData['data']) => {
    const newBreadcrumb: Breadcrumb = {
      id: generateId(),
      level,
      label,
      data,
      timestamp: Date.now(),
    };

    setState(prev => ({
      ...prev,
      breadcrumbs: [...prev.breadcrumbs, newBreadcrumb],
      currentView: level,
      currentData: data,
      error: null,
    }));
  }, []);

  // Open drill-down modal with initial data
  const openDrillDown = useCallback((level: string, label: string, data: DrillDownData['data']) => {
    const initialBreadcrumb: Breadcrumb = {
      id: generateId(),
      level: 'overview',
      label: 'All Data',
      data: null,
      timestamp: Date.now(),
    };

    const targetBreadcrumb: Breadcrumb = {
      id: generateId(),
      level,
      label,
      data,
      timestamp: Date.now(),
    };

    setState({
      breadcrumbs: [initialBreadcrumb, targetBreadcrumb],
      currentView: level,
      currentData: data,
      isOpen: true,
      isLoading: false,
      error: null,
    });
  }, []);

  // Go back one level
  const goBack = useCallback(() => {
    setState(prev => {
      if (prev.breadcrumbs.length <= 1) {
        return { ...defaultState, isOpen: false };
      }

      const newBreadcrumbs = prev.breadcrumbs.slice(0, -1);
      const lastCrumb = newBreadcrumbs[newBreadcrumbs.length - 1];

      return {
        ...prev,
        breadcrumbs: newBreadcrumbs,
        currentView: lastCrumb.level,
        currentData: lastCrumb.data,
        error: null,
      };
    });
  }, []);

  // Go to a specific level in the breadcrumb trail
  const goToLevel = useCallback((levelIndex: number) => {
    setState(prev => {
      if (levelIndex < 0 || levelIndex >= prev.breadcrumbs.length) {
        return prev;
      }

      const newBreadcrumbs = prev.breadcrumbs.slice(0, levelIndex + 1);
      const targetCrumb = newBreadcrumbs[newBreadcrumbs.length - 1];

      return {
        ...prev,
        breadcrumbs: newBreadcrumbs,
        currentView: targetCrumb.level,
        currentData: targetCrumb.data,
        error: null,
      };
    });
  }, []);

  // Reset to initial state
  const reset = useCallback(() => {
    setState(prev => ({
      ...defaultState,
      isOpen: prev.isOpen,
    }));
  }, []);

  // Close drill-down modal
  const closeDrillDown = useCallback(() => {
    setState(defaultState);
  }, []);

  // Set loading state
  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  // Set error state
  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error, isLoading: false }));
  }, []);

  // Generate shareable URL
  const getShareableUrl = useCallback(() => {
    if (state.breadcrumbs.length === 0) return window.location.pathname;

    const params = new URLSearchParams();
    params.set('view', state.currentView);

    // Serialize essential data for URL (avoid large objects)
    if (state.currentData) {
      const urlData: Record<string, string | number> = {};
      const data = state.currentData as Record<string, unknown>;
      if (data.month) urlData.month = String(data.month);
      if (data.category) urlData.category = String(data.category);
      if (data.state) urlData.state = String(data.state);
      if (data.carrier) urlData.carrier = String(data.carrier);
      if (data.vin) urlData.vin = String(data.vin);

      if (Object.keys(urlData).length > 0) {
        params.set('data', JSON.stringify(urlData));
      }
    }

    // Add breadcrumb path
    const path = state.breadcrumbs.map(b => b.label).join(' > ');
    params.set('path', path);

    return `${window.location.pathname}?${params.toString()}`;
  }, [state]);

  // Load state from URL parameters
  const loadFromUrl = useCallback((params: URLSearchParams) => {
    const view = params.get('view');
    const dataStr = params.get('data');
    const path = params.get('path');

    if (!view) return;

    try {
      const data = dataStr ? JSON.parse(dataStr) : null;
      const labels = path ? path.split(' > ') : ['All Data', view];

      const breadcrumbs: Breadcrumb[] = labels.map((label, index) => ({
        id: generateId(),
        level: index === 0 ? 'overview' : (index === labels.length - 1 ? view : 'intermediate'),
        label,
        data: index === labels.length - 1 ? data : null,
        timestamp: Date.now(),
      }));

      setState({
        breadcrumbs,
        currentView: view,
        currentData: data,
        isOpen: true,
        isLoading: false,
        error: null,
      });
    } catch (e) {
      console.error('Failed to load drill-down state from URL:', e);
    }
  }, []);

  // Sync state with URL
  useEffect(() => {
    if (!syncWithUrl || !state.isOpen) return;

    const params = new URLSearchParams();
    if (state.currentView && state.currentView !== 'overview') {
      params.set('drillView', state.currentView);

      if (state.currentData) {
        const urlData: Record<string, string | number> = {};
        const data = state.currentData as Record<string, unknown>;
        if (data.month) urlData.month = String(data.month);
        if (data.category) urlData.category = String(data.category);
        if (data.state) urlData.state = String(data.state);

        if (Object.keys(urlData).length > 0) {
          params.set('drillData', JSON.stringify(urlData));
        }
      }
    }

    // Update URL without adding to history
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  }, [state.currentView, state.currentData, state.isOpen, syncWithUrl]);

  // Load from URL on mount
  useEffect(() => {
    if (!syncWithUrl) return;

    const drillView = searchParams.get('drillView');
    if (drillView) {
      const drillDataStr = searchParams.get('drillData');
      const drillData = drillDataStr ? JSON.parse(drillDataStr) : null;

      openDrillDown(drillView, drillView, drillData);
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!state.isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrillDown();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        if (state.breadcrumbs.length > 1) {
          goBack();
        } else {
          closeDrillDown();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isOpen, state.breadcrumbs.length, closeDrillDown, goBack]);

  const contextValue: DrillDownContextType = {
    ...state,
    drillDown,
    goBack,
    goToLevel,
    reset,
    openDrillDown,
    closeDrillDown,
    setLoading,
    setError,
    getShareableUrl,
    loadFromUrl,
  };

  return (
    <DrillDownContext.Provider value={contextValue}>
      {children}
    </DrillDownContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useDrillDown = (): DrillDownContextType => {
  const context = useContext(DrillDownContext);
  if (!context) {
    throw new Error('useDrillDown must be used within a DrillDownProvider');
  }
  return context;
};

export default DrillDownContext;
